import { TicketSalePageEvent } from 'app/pages/TicketSalePage/slice/types';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { VoucherApplicableTickets, VoucherData } from 'types/CartItem';
import { isSeat, SelectedSeat, SelectedTicket } from 'types/SelectedItem';
import { dateFormatForCalendar } from 'utils/calendar';
import { formatDateAndTimeIntervalOnTimeZone } from 'utils/events';
import { formatMoney } from 'utils/formatMoney';
import { getBundledAddonsForTicketCategory } from 'utils/getBundledAddonsForTicketCategory';
import { EVENT_TYPE_WITH_CALENDAR } from 'utils/static/constants';
import {
  applyDiscountToPrice,
  buildPriceToShow,
  getTotalOfItem,
} from 'utils/tickets';
import BundledAddonsListInsideOrderSummary from '../TicketSalePageTotal/BundledAddonsListInsideOrderSummary';

interface CartTicketProps {
  ticket: SelectedTicket | SelectedSeat;
  event: TicketSalePageEvent;
  voucher: VoucherData;
  voucherApplicableTickets: VoucherApplicableTickets;
}

const CartTicket: React.FC<CartTicketProps> = ({
  ticket,
  event,
  voucher,
  voucherApplicableTickets,
}) => {
  const { t } = useTranslation();

  const { ticketId, name, quantity, price, date, timeStart, timeEnd } = ticket;
  const {
    type,
    tax_rate: { rate },
    currency,
    dateTimeFormat: { timezone, dateFormat, timeFormat },
  } = event;
  const { discount, discount_percent } = voucher;

  const ticketIsDiscountable = ticketId =>
    voucherApplicableTickets.findIndex(id => id === ticketId) >= 0;

  const bundledAddons = getBundledAddonsForTicketCategory(event, ticketId);

  return (
    <div
      key={date ? `${ticketId}-${date}` : ticketId}
      className="py-[8px] border-b border-neutral-40"
    >
      <div className="flex gap-2 justify-between">
        <p className="p2-regular">
          {quantity}
          {' x '}
          {name}{' '}
          {isSeat(ticket) ? (
            <span className="p4-regular text-neutral-800">
              ({ticket.seatId})
            </span>
          ) : null}
          {date && type === EVENT_TYPE_WITH_CALENDAR ? (
            <span className="p4-regular text-neutral-800">
              (
              {DateTime.fromFormat(date, dateFormatForCalendar).toFormat(
                'cccc, LLLL d',
              )}
              )
            </span>
          ) : null}
          {timeStart && timeEnd ? (
            <span className="p4-regular text-neutral-800 block">
              (
              {formatDateAndTimeIntervalOnTimeZone(
                timezone,
                dateFormat,
                timeFormat,
                new Date(date + ' ' + timeStart).toISOString(),
                new Date(date + ' ' + timeEnd).toISOString(),
                t('common.at'),
              )}
              )
            </span>
          ) : null}
        </p>
        <p className="p2-regular">
          {buildPriceToShow(
            getTotalOfItem(
              ticketIsDiscountable(ticketId)
                ? applyDiscountToPrice(price, discount, discount_percent)
                : price,
              quantity,
            ),
            currency,
            formatMoney(0, currency),
          )}
        </p>
      </div>
      {bundledAddons.length > 0 ? (
        <BundledAddonsListInsideOrderSummary
          addons={bundledAddons}
          ticketQuantity={quantity}
        />
      ) : null}
    </div>
  );
};

export default CartTicket;

export const getCartTicketKey = (ticket: SelectedTicket | SelectedSeat) => {
  if (isSeat(ticket)) {
    return `${ticket.ticketId}-${ticket.seatId}`;
  }
  if (ticket.date) {
    return `${ticket.ticketId}-${ticket.date}`;
  }
  return ticket.ticketId;
};
